import React from 'react';
import { Box, Typography } from '@mui/material';
import '../../styles/flashcard-animations.css';

// ---------------------------------------------------------------------------
// 15 progressive milestones (progressively harder to attain)
// ---------------------------------------------------------------------------

const MILESTONES = [
  { at: 1,   msg: 'First card reviewed!',          tier: 1 },
  { at: 3,   msg: 'Getting started!',              tier: 1 },
  { at: 5,   msg: '5 cards down!',                 tier: 1 },
  { at: 10,  msg: '10 cards \u2014 solid session!', tier: 2 },
  { at: 15,  msg: 'Locked in!',                    tier: 1 },
  { at: 20,  msg: '20! Impressive focus',          tier: 2 },
  { at: 25,  msg: 'Quarter century!',              tier: 1 },
  { at: 30,  msg: '30 \u2014 unstoppable!',        tier: 2 },
  { at: 40,  msg: '40 cards reviewed!',            tier: 2 },
  { at: 50,  msg: '50! Scholar mode!',             tier: 3 },
  { at: 75,  msg: '75 \u2014 true champion!',      tier: 3 },
  { at: 100, msg: '100 cards! Legendary!',         tier: 3 },
  { at: 150, msg: '150 \u2014 on fire!',           tier: 3 },
  { at: 200, msg: '200! Language wizard!',         tier: 3 },
  { at: 300, msg: '300! Absolute grandmaster!',    tier: 3 },
];

// ---------------------------------------------------------------------------
// Hook: useSessionMilestones
// ---------------------------------------------------------------------------

export const useSessionMilestones = () => {
  const [gradeCount, setGradeCount] = React.useState(0);
  const [orbAnim, setOrbAnim] = React.useState('');       // 'pulse' | 'nova'
  const [countPopKey, setCountPopKey] = React.useState(null);
  const [milestoneMsg, setMilestoneMsg] = React.useState(null);
  const [barFlashKey, setBarFlashKey] = React.useState(null);
  const [sparksKey, setSparksKey] = React.useState(null);
  const [ringsKey, setRingsKey] = React.useState(null);
  const firedRef = React.useRef(new Set());
  const msgTimerRef = React.useRef(null);

  const checkMilestones = React.useCallback((grade) => {
    setGradeCount((prev) => {
      const next = prev + 1;
      const now = Date.now();

      // Count pop on every grade
      setCountPopKey(now);

      // Check milestones (highest match first)
      let hit = null;
      for (let i = MILESTONES.length - 1; i >= 0; i--) {
        const m = MILESTONES[i];
        if (next >= m.at && !firedRef.current.has(m.at)) {
          firedRef.current.add(m.at);
          hit = m;
          break;
        }
      }

      if (hit) {
        // Milestone hit — supernova orb + rings + sparks + bar flash + message
        setOrbAnim('nova');
        setRingsKey(now);
        setBarFlashKey(now);
        if (hit.tier >= 2) setSparksKey(now);

        // Show message
        if (msgTimerRef.current) clearTimeout(msgTimerRef.current);
        setMilestoneMsg({ msg: hit.msg, key: now });
        msgTimerRef.current = setTimeout(() => setMilestoneMsg(null), 2800);
      } else {
        // Normal grade — subtle orb pulse
        setOrbAnim('pulse');
      }

      // Clear orb anim class after animation completes
      setTimeout(() => setOrbAnim(''), 750);

      return next;
    });
  }, []);

  const resetSession = React.useCallback(() => {
    setGradeCount(0);
    setOrbAnim('');
    setCountPopKey(null);
    setMilestoneMsg(null);
    setBarFlashKey(null);
    setSparksKey(null);
    setRingsKey(null);
    firedRef.current = new Set();
    if (msgTimerRef.current) clearTimeout(msgTimerRef.current);
  }, []);

  return {
    gradeCount,
    orbAnim,
    countPopKey,
    milestoneMsg,
    barFlashKey,
    sparksKey,
    ringsKey,
    checkMilestones,
    resetSession,
    nextMilestone: MILESTONES.find((m) => !firedRef.current.has(m.at)),
  };
};

// ---------------------------------------------------------------------------
// Component: MilestoneBar
// ---------------------------------------------------------------------------

const MilestoneBar = ({
  gradeCount,
  orbAnim,
  countPopKey,
  milestoneMsg,
  barFlashKey,
  sparksKey,
  ringsKey,
  nextMilestone,
}) => {
  const progressPct = nextMilestone
    ? Math.min(100, (gradeCount / nextMilestone.at) * 100)
    : 100;

  const streakActive = gradeCount >= 3;

  return (
    <Box
      className={streakActive ? 'fc-bar-streak' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.5,
        mb: 1,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        position: 'relative',
        overflow: 'visible',
        minHeight: 36,
      }}
    >
      {/* Count */}
      <Box
        key={countPopKey || 'count'}
        className={countPopKey ? 'fc-count-pop' : undefined}
        sx={{ flexShrink: 0, minWidth: 28, textAlign: 'center' }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 900,
            fontSize: '1rem',
            lineHeight: 1,
            background: gradeCount > 0
              ? 'linear-gradient(135deg, #667eea, #764ba2)'
              : 'none',
            WebkitBackgroundClip: gradeCount > 0 ? 'text' : undefined,
            WebkitTextFillColor: gradeCount > 0 ? 'transparent' : undefined,
          }}
        >
          {gradeCount}
        </Typography>
      </Box>

      {/* Progress bar */}
      <Box sx={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
        <div className="fc-progress-track">
          <div
            className="fc-progress-fill"
            style={{ width: `${progressPct}%` }}
          />
          {/* Bar flash on milestone */}
          {barFlashKey && <div key={barFlashKey} className="fc-bar-flash" />}
        </div>

        {/* Next milestone target */}
        {nextMilestone && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 0.75, flexShrink: 0, fontWeight: 600, fontSize: '0.7rem', whiteSpace: 'nowrap' }}
          >
            {nextMilestone.at}
          </Typography>
        )}

        {/* Milestone message overlay */}
        {milestoneMsg && (
          <Box
            key={milestoneMsg.key}
            className="fc-milestone-msg"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translateX(-50%) translateY(-50%)',
              zIndex: 20,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                fontSize: '0.78rem',
                background: 'linear-gradient(135deg, #667eea, #764ba2, #f093fb)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                bgcolor: 'background.paper',
                px: 1.5,
                py: 0.3,
                borderRadius: 1.5,
                boxShadow: '0 2px 16px rgba(102, 126, 234, 0.2)',
              }}
            >
              {milestoneMsg.msg}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Reward orb */}
      <div className={`fc-orb-wrap ${orbAnim === 'nova' ? 'fc-orb-nova' : orbAnim === 'pulse' ? 'fc-orb-pulse' : ''}`}>
        <div className="fc-orb" />

        {/* Expanding rings on milestone */}
        {ringsKey && (
          <React.Fragment key={ringsKey}>
            <div className="fc-ring" />
            <div className="fc-ring fc-ring-2" />
            <div className="fc-ring fc-ring-3" />
          </React.Fragment>
        )}

        {/* Sparkle particles on tier 2+ milestones */}
        {sparksKey && (
          <div key={sparksKey} className="fc-sparks">
            <span /><span /><span /><span /><span /><span />
          </div>
        )}
      </div>
    </Box>
  );
};

export default MilestoneBar;
